import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import Router from 'next/router';

export type CustomAnalytics = {
  identify: (userId: string, properties?: object, options?: object) => void;
  track: (eventName: string, properties?: object) => void;
  setPersonProperties: (properties: object, onceProperties?: object) => void;
};

const initialAnalytics: CustomAnalytics = {
  identify: (userId, properties, options) => {
    posthog.identify(userId, properties, options);
  },
  track: (eventName, properties) => {
    posthog.capture(eventName, properties);
  },
  setPersonProperties: (properties, onceProperties) => {
    posthog.capture('set_person_properties', {
      $set: properties,
      $set_once: onceProperties,
    });
  },
};

const AnalyticsContext = createContext<CustomAnalytics>(initialAnalytics);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host:
          process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://j.easy-peasy.ai',
        // Required when sending through the reverse proxy so links point to
        // the PostHog app instead of the proxy domain.
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        before_send: (event) => {
          // Drop benign ResizeObserver loop notifications. Browsers fire these
          // when a RO callback triggers layout that would re-notify the same
          // observer within a frame; the browser handles it by deferring to
          // the next frame. It is not an error — just noise that floods our
          // exception logs (antd, echarts, PDF viewer, video editor all
          // commonly trigger it).
          if (event && event.event === '$exception') {
            const props = event.properties || {};
            const msg =
              (props['$exception_message'] as string | undefined) ||
              (
                props['$exception_values'] as { value?: string }[] | undefined
              )?.[0]?.value ||
              '';
            if (msg.includes('ResizeObserver loop')) return null;
          }
          return event;
        },
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') posthog.debug();
        },
      });

      // Add page view tracking
      const handleRouteChange = () => posthog?.capture('$pageview');
      Router.events.on('routeChangeComplete', handleRouteChange);

      return () => {
        Router.events.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <AnalyticsContext.Provider value={initialAnalytics}>
        {children}
      </AnalyticsContext.Provider>
    </PostHogProvider>
  );
};

export const useAnalytics = () => useContext(AnalyticsContext);
